import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { equipmentLabelStyle, chemicalLabelStyle } from './styles';

interface ConicalFlaskProps {
  showLiquid?: boolean;
  showZinc?: boolean;
  isReacting?: boolean;
  isPaused?: boolean;
  experimentTime?: number;
  reactionProgress?: number;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  liquidColor?: string;
  liquidLabel?: string;
  liquidFillRatio?: number;
}

function createFlaskGeometry(): THREE.LatheGeometry {
  const points: THREE.Vector2[] = [];
  points.push(new THREE.Vector2(0.001, 0));
  points.push(new THREE.Vector2(0.72, 0));
  points.push(new THREE.Vector2(0.73, 0.03));
  points.push(new THREE.Vector2(0.70, 0.08));
  points.push(new THREE.Vector2(0.64, 0.20));
  points.push(new THREE.Vector2(0.54, 0.38));
  points.push(new THREE.Vector2(0.42, 0.55));
  points.push(new THREE.Vector2(0.30, 0.70));
  points.push(new THREE.Vector2(0.20, 0.82));
  points.push(new THREE.Vector2(0.15, 0.92));
  points.push(new THREE.Vector2(0.13, 1.00));
  points.push(new THREE.Vector2(0.12, 1.05));
  points.push(new THREE.Vector2(0.12, 1.35));
  points.push(new THREE.Vector2(0.14, 1.38));
  points.push(new THREE.Vector2(0.14, 1.40));

  return new THREE.LatheGeometry(points, 48);
}

function createFlaskLiquidGeometry(fillRatio: number): THREE.LatheGeometry {
  const maxHeight = 0.50 * fillRatio;
  if (maxHeight < 0.02) {
    return new THREE.LatheGeometry([new THREE.Vector2(0.001, 0), new THREE.Vector2(0.01, 0)], 4);
  }
  const points: THREE.Vector2[] = [];
  points.push(new THREE.Vector2(0.001, 0.01));

  const contourPoints: [number, number][] = [
    [0.68, 0.01], [0.65, 0.08], [0.60, 0.20], [0.50, 0.38], [0.40, 0.50],
  ];

  for (const [r, h] of contourPoints) {
    if (h <= maxHeight) {
      points.push(new THREE.Vector2(r, h));
    } else {
      const prevIdx = points.length - 1;
      const prevR = points[prevIdx].x;
      const prevH = points[prevIdx].y;
      const t = (maxHeight - prevH) / (h - prevH);
      const interpR = prevR + t * (r - prevR);
      points.push(new THREE.Vector2(interpR, maxHeight));
      break;
    }
  }

  if (points[points.length - 1].y < maxHeight) {
    points.push(new THREE.Vector2(points[points.length - 1].x, maxHeight));
  }
  points.push(new THREE.Vector2(0.001, maxHeight));

  return new THREE.LatheGeometry(points, 48);
}

const ConicalFlask: React.FC<ConicalFlaskProps> = ({
  showLiquid = false, showZinc = false, isReacting = false, isPaused = true, experimentTime = 0, reactionProgress = 0,
  position = [-2.0, -1.72, 0], rotation = [0, 0, 0], scale = 1,
  liquidColor, liquidLabel = 'H₂SO₄', liquidFillRatio,
}) => {
  const bubbleRef = useRef<THREE.InstancedMesh>(null);
  const liquidRef = useRef<THREE.Mesh>(null);
  const zincRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.PointLight>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const flaskGeom = useMemo(() => createFlaskGeometry(), []);

  const bubbles = useMemo(
    () =>
      Array.from({ length: 40 }, () => ({
        pos: new THREE.Vector3(
          (Math.random() - 0.5) * 0.35,
          0.02 + Math.random() * 0.3,
          (Math.random() - 0.5) * 0.35
        ),
        speed: Math.random() * 0.015 + 0.008,
        offset: Math.random() * 100,
        size: Math.random() * 0.015 + 0.01,
      })),
    []
  );

  const acidColor = useMemo(() => new THREE.Color('#ecd44a'), []);
  const productColor = useMemo(() => new THREE.Color('#c8ddd0'), []);
  const currentLiquidColor = useMemo(() => new THREE.Color(), []);

  const liquidFill = showLiquid
    ? (liquidFillRatio !== undefined ? liquidFillRatio : Math.max(0.65, 1.0 - reactionProgress * 0.35))
    : 0;
  const liquidGeom = useMemo(
    () => createFlaskLiquidGeometry(liquidFill || 1.0),
    [liquidFill > 0.01 ? Math.round(liquidFill * 20) / 20 : 0]
  );

  const zincAmount = showZinc ? Math.max(0, 1.0 - reactionProgress * 1.3) : 0;

  useFrame(() => {
    if (bubbleRef.current && isReacting && !isPaused) {
      const rate = Math.max(0.15, 1 - experimentTime / 6);
      bubbles.forEach((b, i) => {
        b.pos.y += b.speed * rate;
        if (b.pos.y > 0.6) {
          b.pos.y = 0.02;
          b.pos.x = (Math.random() - 0.5) * 0.32;
          b.pos.z = (Math.random() - 0.5) * 0.32;
          b.size = Math.random() * 0.015 + 0.01;
        }
        dummy.position.copy(b.pos);
        dummy.scale.setScalar(b.size);
        dummy.updateMatrix();
        bubbleRef.current!.setMatrixAt(i, dummy.matrix);
      });
      bubbleRef.current.instanceMatrix.needsUpdate = true;
    } else if (bubbleRef.current && !isReacting) {
      bubbles.forEach((_, i) => {
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        bubbleRef.current!.setMatrixAt(i, dummy.matrix);
      });
      bubbleRef.current.instanceMatrix.needsUpdate = true;
    }

    if (liquidRef.current && showLiquid) {
      if (liquidColor) {
        (liquidRef.current.material as THREE.MeshStandardMaterial).color.set(liquidColor);
      } else {
        currentLiquidColor.copy(acidColor).lerp(productColor, reactionProgress);
        (liquidRef.current.material as THREE.MeshStandardMaterial).color.copy(currentLiquidColor);
      }
    }

    if (zincRef.current && showZinc) {
      const s = Math.max(0.05, zincAmount);
      zincRef.current.scale.set(s, s, s);
    }

    if (glowRef.current) {
      glowRef.current.intensity = isReacting ? Math.max(0, 0.4 * (1 - reactionProgress * 0.8)) : 0;
    }
  });

  return (
    <group position={position} rotation={rotation} scale={scale}>
      <mesh geometry={flaskGeom}>
        <meshPhysicalMaterial
          color="#e0eef6"
          transparent
          opacity={0.28}
          roughness={0.05}
          metalness={0.0}
          transmission={0.82}
          thickness={0.4}
          ior={1.5}
          envMapIntensity={0.3}
          side={THREE.DoubleSide}
        />
      </mesh>

      <mesh position={[0, 1.39, 0]}>
        <torusGeometry args={[0.13, 0.012, 12, 32]} />
        <meshStandardMaterial color="#b8ccd8" roughness={0.2} metalness={0.1} />
      </mesh>

      {showLiquid && (
        <mesh ref={liquidRef} geometry={liquidGeom} renderOrder={1}>
          <meshPhysicalMaterial
            color={acidColor}
            transparent
            opacity={0.85}
            roughness={0.05}
            metalness={0.0}
            transmission={0.15}
            thickness={0.4}
            ior={1.33}
            emissive="#f0d860"
            emissiveIntensity={0.08}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      )}

      {showZinc && zincAmount > 0.05 && (
        <group position={[0, 0.02, 0]}>
          <mesh ref={zincRef}>
            <sphereGeometry args={[0.22, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color="#7a7a7a" roughness={0.85} metalness={0.55} />
          </mesh>
        </group>
      )}

      <pointLight ref={glowRef} position={[0, 0.3, 0]} color="#f5e6b8" intensity={0} distance={2} decay={2} />

      <instancedMesh ref={bubbleRef} args={[undefined, undefined, 40]}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshStandardMaterial color="#ffffff" transparent opacity={0.45} roughness={0.1} />
      </instancedMesh>

      <Html position={[0, 0.7, 0]} center style={{ pointerEvents: 'none' }}>
        <div style={equipmentLabelStyle}>Conical flask</div>
      </Html>
      {showLiquid && liquidLabel && (
        <Html position={[0, 0.25, 0]} center style={{ pointerEvents: 'none' }}>
          <div style={chemicalLabelStyle}>{liquidLabel}</div>
        </Html>
      )}
      {showZinc && zincAmount > 0.1 && (
        <Html position={[0, 0.08, 0]} center style={{ pointerEvents: 'none' }}>
          <div style={chemicalLabelStyle}>Zn</div>
        </Html>
      )}
    </group>
  );
};

export default ConicalFlask;